import { SavClientResponseBadge } from "@/components/sav/SavClientResponse";
import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, formatDate, getErrorMessage } from "@/lib/api";
import CityAutocomplete from "@/components/CityAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Loader2, Wrench, BarChart3, Users, AlertTriangle, Paperclip
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useTableColumns } from "@/hooks/useTableColumns";
import { ColumnsToggle, SortableTh } from "@/components/TableControls";
import SAVAttachmentsDialog from "@/components/sav/SAVAttachmentsDialog";

const SAV_COLUMNS = [
  { key: "client_name", label: "Client" },
  { key: "client_phone", label: "Téléphone" },
  { key: "description", label: "Description" },
  { key: "order_reference", label: "Commande" },
  { key: "employees", label: "Salariés", sortable: false },
  { key: "subcontractor", label: "Sous-traitant", sortable: false },
  { key: "cost", label: "Coût" },
  { key: "duration_days", label: "Jours" },
  { key: "intervention_date", label: "Date" },
  { key: "status", label: "Statut" },
  { key: "actions", label: "Actions", sortable: false, always: true },
];

const STATUS_MAP = {
  planifie: { label: "Planifié", color: "bg-blue-100 text-blue-800" },
  en_cours: { label: "En cours", color: "bg-amber-100 text-amber-800" },
  termine: { label: "Terminé", color: "bg-green-100 text-green-800" },
  annule: { label: "Annulé", color: "bg-red-100 text-red-800" }
};

const defaultForm = {
  client_name: "", description: "", order_id: "", employee_ids: [],
  subcontractor_ids: [], cost: "", duration_days: "1", operators: "1",
  intervention_date: "", status: "planifie", notes: "",
  address: "", city: "",
  repairer_employee_id: "", repairer_subcontractor_id: ""
};

export const SAVPage = () => {
  const [savs, setSavs] = useState([]);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({ ...defaultForm });
  const [activeTab, setActiveTab] = useState("list");
  const [showDone, setShowDone] = useState(false);
  const [attachmentsSav, setAttachmentsSav] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [savRes, statsRes, ordRes, empRes, subRes] = await Promise.all([
        api.getSAVs(),
        api.getSAVStatsByEmployee(),
        api.getOrders(),
        api.getEmployees(),
        api.getSubcontractors()
      ]);
      setSavs(savRes.data);
      setStats(statsRes.data);
      setOrders(ordRes.data);
      setEmployees(empRes.data);
      setSubcontractors(subRes.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Deep-link from QR code (?focus=<sav_id>) → auto-open attachments dialog
  useEffect(() => {
    if (loading || savs.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus");
    if (focusId) {
      const target = savs.find((s) => s.id === focusId);
      if (target) {
        setAttachmentsSav(target);
        // Clean URL once dialog is opened
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, savs.length]);

  const {
    visibleColumns: visibleSavColumns,
    isVisible: isSavColVisible,
    toggleVisibility: toggleSavCol,
    handleSort: handleSavSort,
    sortArrow: savSortArrow,
    sortItems: sortSavs,
    orderedColumns: orderedSavColumns,
    moveColumn: moveSavColumn,
    resetOrder: resetSavOrder,
  } = useTableColumns({ storageKey: "savs-table", columns: SAV_COLUMNS });

  const openCreate = () => {
    setEditingItem(null);
    setFormData({ ...defaultForm });
    setDialogOpen(true);
  };

  // Attachments dialog
  const openAttachments = (sav) => setAttachmentsSav(sav);
  const closeAttachments = () => {
    setAttachmentsSav(null);
    fetchAll(); // refresh attachment_count
  };

  const openEdit = (sav) => {
    setEditingItem(sav);
    const subIds = Array.isArray(sav.subcontractor_ids) && sav.subcontractor_ids.length > 0
      ? sav.subcontractor_ids
      : (sav.subcontractor_id ? [sav.subcontractor_id] : []);
    setFormData({
      client_name: sav.client_name || "",
      description: sav.description || "",
      order_id: sav.order_id || "",
      employee_ids: sav.employee_ids || [],
      subcontractor_ids: subIds,
      cost: sav.cost || "",
      duration_days: sav.duration_days || "1",
      operators: sav.operators || "1",
      intervention_date: sav.intervention_date || "",
      status: sav.status || "planifie",
      notes: sav.notes || "",
      address: sav.address || "",
      city: sav.city || "",
      repairer_employee_id: sav.repairer_employee_id || "",
      repairer_subcontractor_id: sav.repairer_subcontractor_id || ""
    });
    setDialogOpen(true);
  };

  const toggleEmployee = (empId) => {
    setFormData(prev => ({
      ...prev,
      employee_ids: prev.employee_ids.includes(empId)
        ? prev.employee_ids.filter(id => id !== empId)
        : [...prev.employee_ids, empId]
    }));
  };

  const toggleSubcontractor = (subId) => {
    setFormData(prev => ({
      ...prev,
      subcontractor_ids: prev.subcontractor_ids.includes(subId)
        ? prev.subcontractor_ids.filter(id => id !== subId)
        : [...prev.subcontractor_ids, subId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name.trim() || !formData.description.trim()) {
      toast.error("Client et description obligatoires");
      return;
    }
    try {
      const payload = {
        ...formData,
        cost: parseFloat(formData.cost) || 0,
        duration_days: parseFloat(formData.duration_days) || 1,
        operators: parseInt(formData.operators) || 1,
        order_id: formData.order_id || null,
        subcontractor_id: formData.subcontractor_ids[0] || null,
        subcontractor_ids: formData.subcontractor_ids,
        repairer_employee_id: formData.repairer_employee_id || null,
        repairer_subcontractor_id: formData.repairer_subcontractor_id || null,
        intervention_date: formData.intervention_date || null
      };
      if (editingItem) {
        await api.updateSAV(editingItem.id, payload);
        toast.success("SAV modifié");
      } else {
        await api.createSAV(payload);
        toast.success("SAV créé");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await api.deleteSAV(itemToDelete.id);
      toast.success("SAV supprimé");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchAll();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const totalCost = savs.reduce((s, v) => s + (v.cost || 0), 0);
  const plannedCount = savs.filter(s => s.status === "planifie").length;
  const doneCount = savs.filter(s => s.status === "termine").length;

  return (
    <div className="space-y-6 animate-in" data-testid="sav-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">SAV</h1>
          <p className="text-muted-foreground mt-1">Service après-vente et interventions</p>
        </div>
        <Button onClick={openCreate} data-testid="add-sav-btn">
          <Plus className="h-4 w-4 mr-2" />Nouveau SAV
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total SAV</p>
          <p className="text-2xl font-heading font-bold mt-1" data-testid="kpi-total-sav">{savs.length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Coût total</p>
          <p className="text-2xl font-heading font-bold mt-1 font-mono" data-testid="kpi-total-cost">{formatCurrency(totalCost)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Planifiés</p>
          <p className="text-2xl font-heading font-bold mt-1 text-blue-600">{plannedCount}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Terminés</p>
          <p className="text-2xl font-heading font-bold mt-1 text-green-600">{doneCount}</p>
        </CardContent></Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-sav-list"><Wrench className="h-4 w-4 mr-1" /> Liste</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-sav-stats"><BarChart3 className="h-4 w-4 mr-1" /> Statistiques</TabsTrigger>
        </TabsList>

        {/* LIST */}
        <TabsContent value="list">
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-4 pt-3">
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="show-done-savs-toggle">
                  <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} className="h-4 w-4" />
                  Afficher les SAV terminés / annulés
                </label>
                {savs.length > 0 && (
                  <ColumnsToggle columns={SAV_COLUMNS} orderedColumns={orderedSavColumns} isVisible={isSavColVisible} toggleVisibility={toggleSavCol} moveColumn={moveSavColumn} resetOrder={resetSavOrder} testId="savs-columns-toggle" />
                )}
              </div>
              {(() => {
                const visibleSavs = savs.filter(s => showDone || !["termine", "annule"].includes(s.status));
                if (savs.length === 0) {
                  return (
                    <div className="text-center py-12 text-muted-foreground">
                      <Wrench className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>Aucun SAV enregistré</p>
                    </div>
                  );
                }
                if (visibleSavs.length === 0) {
                  return (
                    <div className="text-center py-12 text-muted-foreground">
                      <Wrench className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>Aucun SAV actif. Activez "Afficher les SAV terminés / annulés" pour voir les anciens.</p>
                    </div>
                  );
                }
                return (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        {visibleSavColumns.map(col => (
                          <SortableTh key={col.key} columnKey={col.key} label={col.label} sortable={col.sortable !== false} sortArrow={savSortArrow} handleSort={handleSavSort} />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortSavs(visibleSavs, {
                        status: (s) => STATUS_MAP[s.status]?.label || s.status,
                      }).map(sav => {
                        const renderSavCell = (key) => {
                          switch(key) {
                            case 'client_name': return (
                              <td key={key} className="font-medium">
                                <div>{sav.client_name}</div>
                                {sav.client_email && <div className="text-[10px] text-muted-foreground">{sav.client_email}</div>}
                              </td>
                            );
                            case 'client_phone': return (
                              <td key={key} className="text-xs font-mono">
                                {sav.client_phone ? <a href={`tel:${sav.client_phone}`} className="text-blue-600 hover:underline">{sav.client_phone}</a> : "-"}
                              </td>
                            );
                            case 'description': return <td key={key} className="max-w-[200px] truncate">{sav.description}</td>;
                            case 'order_reference': return <td key={key}>{sav.order_reference ? <Badge variant="outline" className="font-mono text-xs">{sav.order_reference}</Badge> : "-"}</td>;
                            case 'employees': return (
                              <td key={key}>
                                <div className="flex flex-wrap gap-1">
                                  {(sav.employee_names || []).map((n, i) => <Badge key={i} variant="secondary" className="text-xs">{n}</Badge>)}
                                  {(sav.employee_names || []).length === 0 && "-"}
                                </div>
                              </td>
                            );
                            case 'subcontractor': return (
                              <td key={key}>
                                <div className="flex flex-wrap gap-1">
                                  {(sav.subcontractor_names || (sav.subcontractor_name ? [sav.subcontractor_name] : [])).map((n, i) => <Badge key={i} variant="outline" className="text-xs">{n}</Badge>)}
                                  {!(sav.subcontractor_names?.length || sav.subcontractor_name) && "-"}
                                </div>
                              </td>
                            );
                            case 'cost': return <td key={key} className="font-mono font-bold">{formatCurrency(sav.cost)}</td>;
                            case 'duration_days': return <td key={key}>{sav.duration_days}j / {sav.operators}op</td>;
                            case 'intervention_date': return <td key={key}>{sav.intervention_date ? formatDate(sav.intervention_date) : "-"}</td>;
                            case 'status': return <td key={key}><Badge className={STATUS_MAP[sav.status]?.color || ""}>{STATUS_MAP[sav.status]?.label || sav.status}</Badge><SavClientResponseBadge sav={sav} onSeen={fetchAll} /></td>;
                            case 'actions': return (
                              <td key={key}>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openAttachments(sav)} title="Photos / Documents / PDF">
                                    <Paperclip className="h-4 w-4" />
                                    {sav.attachment_count > 0 && (
                                      <span className="absolute -top-1 -right-1 bg-primary text-white rounded-full text-[9px] w-3.5 h-3.5 flex items-center justify-center">
                                        {sav.attachment_count}
                                      </span>
                                    )}
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(sav)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setItemToDelete(sav); setDeleteDialogOpen(true); }}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            );
                            default: return null;
                          }
                        };
                        return (
                          <tr key={sav.id} data-testid={`sav-row-${sav.id}`}>
                            {visibleSavColumns.map(col => renderSavCell(col.key))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* STATS */}
        <TabsContent value="stats">
          {stats && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader><CardTitle className="text-base">SAV par salarié</CardTitle></CardHeader>
                  <CardContent>
                    {stats.by_employee.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">Aucun SAV assigné</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Salarié</TableHead>
                            <TableHead>Nb SAV</TableHead>
                            <TableHead>Coût total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stats.by_employee.map(e => (
                            <TableRow key={e.employee_id}>
                              <TableCell className="font-medium">{e.employee_name}</TableCell>
                              <TableCell>{e.count}</TableCell>
                              <TableCell className="font-mono font-bold">{formatCurrency(e.total_cost)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-base">Coût SAV par salarié</CardTitle></CardHeader>
                  <CardContent>
                    {stats.by_employee.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">Aucune donnée</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={Math.max(200, stats.by_employee.length * 50)}>
                        <BarChart data={stats.by_employee} layout="vertical" margin={{ left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" tickFormatter={v => `${v} €`} />
                          <YAxis type="category" dataKey="employee_name" width={120} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(v) => formatCurrency(v)} />
                          <Bar dataKey="total_cost" fill="#C05621" radius={[0, 4, 4, 0]} name="Coût total" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="pt-5">
                  <div className="flex items-center gap-6 text-sm">
                    <div><span className="text-muted-foreground">Total SAV :</span> <span className="font-bold">{stats.total_savs}</span></div>
                    <div><span className="text-muted-foreground">Coût total :</span> <span className="font-bold font-mono">{formatCurrency(stats.total_cost)}</span></div>
                    <div><span className="text-muted-foreground">Coût moyen :</span> <span className="font-bold font-mono">{stats.total_savs > 0 ? formatCurrency(stats.total_cost / stats.total_savs) : "0,00 €"}</span></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingItem ? "Modifier le SAV" : "Nouveau SAV"}</DialogTitle>
            <DialogDescription>Informations de l'intervention SAV</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Input value={formData.client_name} onChange={e => setFormData({...formData, client_name: e.target.value})} data-testid="sav-client" />
              </div>
              <div className="space-y-2">
                <Label>Commande liée</Label>
                {(() => {
                  const selected = orders.find((o) => o.id === formData.order_id);
                  if (selected && !formData._order_search) {
                    return (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-indigo-300 bg-indigo-50 text-indigo-800 truncate max-w-[400px]" data-testid="sav-order-selected">
                          {selected.reference} — {selected.client_name}
                        </Badge>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setFormData({ ...formData, order_id: "", _order_search: "" })}
                        >Changer</Button>
                      </div>
                    );
                  }
                  const q = (formData._order_search || "").trim().toLowerCase();
                  const filtered = q
                    ? orders.filter((o) => {
                        const ref = (o.reference || "").toLowerCase();
                        const client = (o.client_name || "").toLowerCase();
                        return ref.includes(q) || client.includes(q);
                      }).slice(0, 20)
                    : [];
                  return (
                    <div className="relative">
                      <Input
                        placeholder="Tapez un n° de commande ou un nom de client (laisser vide = aucune)"
                        value={formData._order_search || ""}
                        onChange={(e) => setFormData({ ...formData, _order_search: e.target.value, order_id: "" })}
                        autoComplete="off"
                        data-testid="sav-order-search"
                      />
                      {q && filtered.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white border rounded-md shadow-lg">
                          {filtered.map((o) => (
                            <button
                              key={o.id}
                              type="button"
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 border-b last:border-b-0"
                              onClick={() => setFormData((prev) => ({
                                ...prev,
                                order_id: o.id,
                                _order_search: "",
                                client_name: prev.client_name || o.client_name || "",
                                city: prev.city || o.city || "",
                              }))}
                              data-testid={`sav-order-option-${o.id}`}
                            >
                              <span className="font-mono text-xs text-indigo-700">{o.reference}</span>
                              <span className="text-muted-foreground"> — </span>
                              <span>{o.client_name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {q && filtered.length === 0 && (
                        <div className="absolute z-20 mt-1 w-full px-3 py-2 text-xs text-muted-foreground bg-white border rounded-md shadow-lg">
                          Aucune commande correspondante.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Description *</Label>
                <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} data-testid="sav-desc" />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Adresse</Label>
                <Input
                  value={formData.address}
                  onChange={e => setFormData({...formData, address: e.target.value})}
                  placeholder="12 rue des Lilas"
                  data-testid="sav-address"
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Ville / Code postal</Label>
                <CityAutocomplete
                  value={formData.city}
                  onChange={(v) => setFormData({...formData, city: v})}
                  placeholder="Ville ou code postal..."
                  data-testid="sav-city"
                />
              </div>
              <div className="space-y-2">
                <Label>Coût (EUR)</Label>
                <Input type="number" step="0.01" value={formData.cost} onChange={e => setFormData({...formData, cost: e.target.value})} data-testid="sav-cost" />
              </div>
              <div className="space-y-2">
                <Label>Nombre de jours</Label>
                <Input type="number" step="0.5" value={formData.duration_days} onChange={e => setFormData({...formData, duration_days: e.target.value})} data-testid="sav-days" />
              </div>
              <div className="space-y-2">
                <Label>Opérateurs</Label>
                <Input type="number" value={formData.operators} onChange={e => setFormData({...formData, operators: e.target.value})} data-testid="sav-operators" />
              </div>
              <div className="space-y-2">
                <Label>Date d'intervention</Label>
                <Input type="date" value={formData.intervention_date} onChange={e => setFormData({...formData, intervention_date: e.target.value})} data-testid="sav-date" />
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                  <SelectTrigger data-testid="sav-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Employee multi-select */}
            <div className="space-y-2">
              <Label>Salariés assignés</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                {employees.filter(e => !e.exit_date).map(emp => (
                  <div key={emp.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`emp-sav-${emp.id}`}
                      checked={formData.employee_ids.includes(emp.id)}
                      onCheckedChange={() => toggleEmployee(emp.id)}
                      data-testid={`sav-emp-${emp.id}`}
                    />
                    <label htmlFor={`emp-sav-${emp.id}`} className="text-sm cursor-pointer">
                      {emp.first_name} {emp.last_name}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Subcontractor multi-select */}
            <div className="space-y-2">
              <Label>Sous-traitants assignés</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                {subcontractors.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Aucun sous-traitant configuré</p>
                ) : subcontractors.map(s => {
                  const label = s.company_name || `${s.first_name || ""} ${s.last_name || ""}`.trim() || "—";
                  return (
                    <div key={s.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`sub-sav-${s.id}`}
                        checked={formData.subcontractor_ids.includes(s.id)}
                        onCheckedChange={() => toggleSubcontractor(s.id)}
                        data-testid={`sav-sub-${s.id}`}
                      />
                      <label htmlFor={`sub-sav-${s.id}`} className="text-sm cursor-pointer">
                        {label}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Réparateur (informatif — distinct du Responsable financier) */}
            <div className="border rounded-lg p-3 bg-amber-50/30 space-y-3" data-testid="sav-repairer-section">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-amber-900">Réparateur (informatif)</Label>
                <span className="text-[11px] text-muted-foreground italic">
                  Différent du Responsable — sans impact financier
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Qui intervient réellement sur le terrain ? Vous pouvez sélectionner un salarié et/ou un sous-traitant.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Salarié réparateur</Label>
                  <Select
                    value={formData.repairer_employee_id || "__none__"}
                    onValueChange={(v) => setFormData({ ...formData, repairer_employee_id: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="sav-repairer-employee">
                      <SelectValue placeholder="Aucun" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Aucun —</SelectItem>
                      {employees.filter(e => !e.exit_date).map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Sous-traitant réparateur</Label>
                  <Select
                    value={formData.repairer_subcontractor_id || "__none__"}
                    onValueChange={(v) => setFormData({ ...formData, repairer_subcontractor_id: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="sav-repairer-subcontractor">
                      <SelectValue placeholder="Aucun" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Aucun —</SelectItem>
                      {subcontractors.map(s => {
                        const label = s.company_name || `${s.first_name || ""} ${s.last_name || ""}`.trim() || "—";
                        return (
                          <SelectItem key={s.id} value={s.id}>{label}</SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="sav-submit">{editingItem ? "Modifier" : "Créer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce SAV ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-sav">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SAVAttachmentsDialog
        open={!!attachmentsSav}
        onClose={closeAttachments}
        sav={attachmentsSav}
      />
    </div>
  );
};

export default SAVPage;
